import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import UnprotectedRoute from './components/UnprotectedRoute';
import { TutorialProvider } from './context/TutorialContext';
import { ConfirmProvider } from './components/ui/ConfirmDialog';
import DesktopAppsOnboardingNudge from './components/DesktopAppsOnboardingNudge';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import KnowledgeBase from './pages/KnowledgeBase';
import Transcripts from './pages/Transcripts';
import Settings from './pages/Settings';
import Users from './pages/Users';
import Sessions from './pages/Sessions';
import SessionDetail from './pages/SessionDetail';
import Subscription from './pages/Subscription';
import SupportTickets from './pages/SupportTickets';
import Calendar from './pages/Calendar';
import Training from './pages/Training';
import TrainingTrackBuilder from './pages/TrainingTrackBuilder';
import TrainingSessionEvaluation from './pages/TrainingSessionEvaluation';
import BulkUpload from './pages/BulkUpload';
import ActionsCenter from './pages/ActionsCenter';
import { store } from './store';
import { useRoleAccess } from './hooks/useRoleAccess';
import UpdateAvailableBanner from './components/UpdateAvailableBanner';

/** Redirects to /knowledge-base for super admins, /dashboard for everyone else */
const DefaultRedirect: React.FC = () => {
  const { isSuperAdmin } = useRoleAccess();
  return <Navigate to={isSuperAdmin ? '/knowledge-base' : '/dashboard'} replace />;
};

// Mirrors the signature, ink, status, hairline and canvas tokens in
// src/styles/tokens.css (Foundry copper, graphite ink headings). MUI's
// palette and style overrides need literal hex/px for their internal color
// math, so this is a literal mirror, not a live CSS-var reference. This is
// what keeps MUI-heavy pages (e.g. the training pages) visually consistent
// with the Tailwind-styled pages without editing every Typography/Button
// call site individually.
const theme = createTheme({
  palette: {
    primary: {
      main: '#A6531E',
      dark: '#8A4417',
    },
    secondary: {
      main: '#423F39',
    },
    // Status axis: MUI's stock green/orange/red/blue are not ours. Without
    // these every Chip, Alert and LinearProgress on an MUI page painted
    // Material colours while the Tailwind pages painted the tokens.
    success: {
      main: '#2F7D55',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#B77E1E',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#BC3B2E',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#35617F',
      contrastText: '#FFFFFF',
    },
    text: {
      primary: '#1C1B18',
      // --ink-muted, not --ink-secondary: this colour drives every caption and
      // field helperText, and --ink-secondary was indistinguishable from the
      // --ink headings. --ink-muted still clears 4.5:1 on white.
      secondary: '#6B675E',
      disabled: '#A8A296',
    },
    divider: '#E6E1D7',
    background: {
      default: '#FAF8F4',
      paper: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      'Oxygen',
      'Ubuntu',
      'Cantarell',
      '"Fira Sans"',
      '"Droid Sans"',
      '"Helvetica Neue"',
      'sans-serif',
    ].join(','),
    h1: { fontWeight: 600, color: '#1C1B18' },
    h2: { fontWeight: 600, color: '#1C1B18' },
    h3: { fontWeight: 600, color: '#1C1B18' },
    h4: { fontWeight: 600, color: '#1C1B18' },
    h5: { fontWeight: 500, color: '#1C1B18' },
    h6: { fontWeight: 500, color: '#1C1B18' },
  },
  shape: {
    borderRadius: 8, // matches --radius-control; card-level overrides below
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 16, // matches --radius-card
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16, // matches --radius-card
          border: '1px solid #E6E1D7', // matches --hairline
        },
      },
    },
  },
});

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <Router>
            <ConfirmProvider>
            <TutorialProvider>
            <Routes>
              <Route path="/" element={<DefaultRedirect />} />
              <Route 
                path="/login" 
                element={
                  <UnprotectedRoute>
                    <Login />
                  </UnprotectedRoute>
                } 
              />
              <Route 
                path="/signup" 
                element={
                  <UnprotectedRoute>
                    <Signup />
                  </UnprotectedRoute>
                } 
              />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/knowledge-base"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute requiredRole="admin">
                      <KnowledgeBase />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/actions"
                element={
                  <ProtectedRoute>
                    <ActionsCenter />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bulk-upload"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute requiredRole="admin">
                      <BulkUpload />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transcripts" 
                element={
                  <ProtectedRoute>
                    <Transcripts />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/settings" 
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute requiredRole="admin">
                      <Users />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/sessions" 
                element={
                  <ProtectedRoute>
                    <Sessions />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/sessions/:sessionId"
                element={
                  <ProtectedRoute>
                    <SessionDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute requiredRole="admin">
                      <Subscription />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/support-tickets"
                element={
                  <ProtectedRoute>
                    <SupportTickets />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/calendar"
                element={
                  <ProtectedRoute>
                    <Calendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training"
                element={
                  <ProtectedRoute>
                    <Training />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training/builder"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute requiredRole="admin">
                      <TrainingTrackBuilder />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training/builder/:trackId"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute requiredRole="admin">
                      <TrainingTrackBuilder />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training/sessions/:sessionId"
                element={
                  <ProtectedRoute>
                    <TrainingSessionEvaluation />
                  </ProtectedRoute>
                }
              />
              <Route path="/dictionary" element={<Navigate to="/knowledge-base" replace />} />
              <Route path="*" element={<DefaultRedirect />} />
            </Routes>
            {/* One-time desktop-apps nudge after onboarding; mounted above the
                routes so completeTutorial's navigation cannot unmount it. */}
            <DesktopAppsOnboardingNudge />
            {/* New-build prompt: pinned on every route, including login */}
            <UpdateAvailableBanner />
            </TutorialProvider>
            </ConfirmProvider>
          </Router>
        </ErrorBoundary>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
